import { useListContext } from "ra-core";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { offerColor, useOffers } from "../useOffers";
import { ESTADOS, RATINGS, SECTORES, ZONAS } from "./directoryFilters";
import { useReviewThemes } from "../reviews/reviews";
import { HelpTooltip } from "../help/HelpTooltip";
import { useIsDueno } from "../useIsDueno";

export const FUENTES: [string, string][] = [
  ["denue_2026_05", "DENUE 05/2026"],
  ["denue_api", "DENUE (API)"],
  ["manual", "Manual"],
  ["claude", "Claude"],
  ["ia", "Empresa con IA"],
  ["web", "Sitio web"],
];

type F = Record<string, any>;

/** Lee y cambia los filtros de la lista (quita la clave si queda vacía). */
export const useF = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const f: F = filterValues ?? {};
  const set = (patch: F) => {
    const next = { ...f, ...patch };
    for (const [k, v] of Object.entries(next))
      if (
        v === undefined ||
        v === "" ||
        v === null ||
        (Array.isArray(v) && !v.length)
      )
        delete next[k];
    setFilters(next, displayedFilters);
  };
  const toggle = (key: string, v: string) => {
    const arr: string[] = f[key] ?? [];
    set({ [key]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] });
  };
  return { f, set, toggle };
};

const Chips = ({
  k,
  values,
  label,
  color,
}: {
  k: string;
  values: string[];
  label?: (v: string) => string;
  color?: (v: string) => string;
}) => {
  const { f, toggle } = useF();
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map((v) => {
        const on = (f[k] ?? []).includes(v);
        return (
          <button
            key={v}
            type="button"
            onClick={() => toggle(k, v)}
            className={cn(
              "flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold",
              on
                ? "border-primary bg-accent text-accent-foreground"
                : "bg-card",
            )}
          >
            {color && (
              <span
                className="size-2 rounded-sm"
                style={{ background: color(v) }}
              />
            )}
            {label ? label(v) : v}
          </button>
        );
      })}
    </div>
  );
};

const Section = ({
  title,
  n,
  children,
  open,
}: {
  title: string;
  n?: number;
  children: React.ReactNode;
  open?: boolean;
}) => (
  <details className="group border-t" open={open}>
    <summary className="flex cursor-pointer list-none items-center justify-between px-3.5 py-2.5 font-bold [&::-webkit-details-marker]:hidden">
      {title}
      <span className="flex items-center gap-2">
        {!!n && (
          <span className="rounded-full bg-primary px-1.5 text-[11px] font-extrabold text-primary-foreground">
            {n}
          </span>
        )}
        <ChevronDown className="size-4 transition-transform group-open:rotate-180" />
      </span>
    </summary>
    <div className="grid gap-2 px-3.5 pb-3 text-sm">{children}</div>
  </details>
);

const Hint = ({ children }: { children: React.ReactNode }) => (
  <span className="text-xs text-[var(--faint)]">{children}</span>
);
const Check = ({
  k,
  label,
  invert,
}: {
  k: string;
  label: string;
  invert?: boolean;
}) => {
  const { f, set } = useF();
  const on = invert ? f[k] !== false : !!f[k];
  return (
    <label className="flex items-center gap-2">
      <input
        type="checkbox"
        className="size-4 accent-[var(--primary)]"
        checked={on}
        onChange={(e) =>
          set({
            [k]: invert
              ? e.target.checked
                ? undefined
                : false
              : e.target.checked || undefined,
          })
        }
      />
      {label}
    </label>
  );
};
const count = (f: F, keys: string[]) =>
  keys.filter(
    (k) => f[k] !== undefined && !(Array.isArray(f[k]) && !f[k].length),
  ).length;

/** "Más filtros" del Directorio (PRD D.1). Reseñas llegan en H. */
export const FiltersPanel = ({ onClose }: { onClose?: () => void }) => {
  const { f, set } = useF();
  const { data: offers } = useOffers();
  const isDueno = useIsDueno();
  const estadoUnico = f._estados?.length === 1 ? f._estados[0] : f.estado;
  const { data: municipios } = useQuery({
    queryKey: ["municipios", estadoUnico],
    enabled: !!estadoUnico,
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data } = await getSupabaseClient()
        .from("directory_municipios")
        .select("municipio, empresas")
        .eq("estado", estadoUnico)
        .order("municipio");
      return data ?? [];
    },
  });

  return (
    <div className="flex flex-col rounded-xl border bg-card py-1.5">
      <div
        className={cn(
          "flex items-center justify-between px-3.5 py-2",
          onClose && "pr-12",
        )}
      >
        <span className="inline-flex items-center gap-1">
          <b className="text-[15px]">Filtros</b>
          <HelpTooltip topic="directory.filters" />
        </span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              set(Object.fromEntries(Object.keys(f).map((k) => [k, undefined])))
            }
          >
            Limpiar
          </Button>
          {onClose && (
            <Button size="sm" onClick={onClose}>
              Ver resultados
            </Button>
          )}
        </div>
      </div>
      <Section
        title="Oferta donde encaja"
        n={count(f, ["_ofertas", "fits@cs"])}
        open
      >
        <Chips
          k="_ofertas"
          values={(offers ?? []).map((o) => o.slug)}
          label={(v) => offers?.find((o) => o.slug === v)?.name ?? v}
          color={(v) => offerColor({ slug: v })}
        />
        <Hint>Reglas de segmento de cada producto</Hint>
      </Section>
      <Section
        title="Ubicación"
        n={count(f, ["_zona", "_estados", "municipio", "_cp"])}
        open
      >
        <Hint>Zona</Hint>
        <Chips k="_zona" values={Object.keys(ZONAS)} />
        <Hint>Estado</Hint>
        <Chips k="_estados" values={ESTADOS} />
        <label className="grid gap-1">
          Municipio
          <select
            className="h-9 rounded-lg border bg-card px-2"
            disabled={!estadoUnico}
            value={f.municipio ?? ""}
            onChange={(e) => set({ municipio: e.target.value || undefined })}
          >
            <option value="">
              {estadoUnico ? "Todos" : "Elige un solo estado primero"}
            </option>
            {municipios?.map((m) => (
              <option key={m.municipio} value={m.municipio}>
                {m.municipio} ({m.empresas.toLocaleString("es-MX")})
              </option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="grid gap-1">
            C.P.
            <Input
              value={f._cp ?? ""}
              inputMode="numeric"
              placeholder="76000"
              onChange={(e) =>
                set({
                  _cp:
                    e.target.value.replace(/\D/g, "").slice(0, 5) || undefined,
                })
              }
            />
          </label>
          <label className="grid gap-1">
            Radio km
            <Input
              value={f._radio_km ?? ""}
              inputMode="numeric"
              placeholder="10"
              onChange={(e) =>
                set({
                  _radio_km: e.target.value.replace(/\D/g, "") || undefined,
                })
              }
            />
          </label>
        </div>
        <Hint>
          El radio es aproximado: un cuadro alrededor del centro del C.P.
        </Hint>
      </Section>
      <Section title="Giro" n={count(f, ["_sectores", "_scian", "sector"])}>
        <Chips k="_sectores" values={SECTORES} />
        <label className="grid gap-1">
          SCIAN (código o inicio)
          <Input
            value={f._scian ?? ""}
            inputMode="numeric"
            placeholder="4311"
            onChange={(e) =>
              set({ _scian: e.target.value.replace(/\D/g, "") || undefined })
            }
          />
        </label>
      </Section>
      <Section
        title="Tamaño"
        n={count(f, ["_tamanos", "_emp_min", "_emp_max", "tamano"])}
      >
        <Chips
          k="_tamanos"
          values={["Micro", "Pequeña", "Mediana", "Grande"]}
        />
        <div className="grid grid-cols-2 gap-2">
          <label className="grid gap-1">
            Empleados mín.
            <Input
              type="number"
              min={0}
              value={f._emp_min ?? ""}
              onChange={(e) => set({ _emp_min: e.target.value || undefined })}
            />
          </label>
          <label className="grid gap-1">
            Empleados máx.
            <Input
              type="number"
              min={0}
              value={f._emp_max ?? ""}
              onChange={(e) => set({ _emp_max: e.target.value || undefined })}
            />
          </label>
        </div>
      </Section>
      <Section
        title="Calidad del prospecto"
        n={count(f, ["_tiers", "_prio_min", "tier"])}
      >
        <Chips
          k="_tiers"
          values={["A", "B", "C"]}
          label={(v) => `Nivel ${v}`}
        />
        <label className="grid gap-1">
          Prioridad mínima: <b>{f._prio_min ?? 0}</b>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={f._prio_min ?? 0}
            className="accent-[var(--primary)]"
            onChange={(e) =>
              set({ _prio_min: Number(e.target.value) || undefined })
            }
          />
        </label>
      </Section>
      <Section
        title="Datos de contacto"
        n={count(f, [
          "_tel",
          "_email",
          "_web",
          "_dominio_sin_web",
          "email_tipo",
        ])}
      >
        <Check k="_tel" label="Tiene teléfono" />
        <Check k="_email" label="Tiene correo" />
        <Check k="_web" label="Tiene sitio web" />
        <Check k="_dominio_sin_web" label="Dominio propio pero sin sitio" />
        <label className="grid gap-1">
          Tipo de correo
          <select
            className="h-9 rounded-lg border bg-card px-2"
            value={f.email_tipo ?? ""}
            onChange={(e) => set({ email_tipo: e.target.value || undefined })}
          >
            <option value="">Todos</option>
            <option value="corporativo">Corporativo</option>
            <option value="personal">Personal</option>
            <option value="sin_email">Sin correo</option>
          </select>
        </label>
      </Section>
      <Section title="Antigüedad" n={count(f, ["_alta_desde", "_alta_hasta"])}>
        <div className="grid grid-cols-2 gap-2">
          <label className="grid gap-1">
            Alta desde
            <Input
              type="number"
              placeholder="2010"
              value={f._alta_desde ?? ""}
              onChange={(e) =>
                set({ _alta_desde: e.target.value || undefined })
              }
            />
          </label>
          <label className="grid gap-1">
            Hasta
            <Input
              type="number"
              placeholder="2026"
              value={f._alta_hasta ?? ""}
              onChange={(e) =>
                set({ _alta_hasta: e.target.value || undefined })
              }
            />
          </label>
        </div>
        <Hint>Año de registro en el DENUE</Hint>
      </Section>
      <Section
        title="Estado en el CRM"
        n={count(f, ["company_id@is", "_ocultar_dnc"])}
      >
        {/* owner only: for a freelancer "in the CRM" may mean another workspace (see useInCrm) */}
        {(isDueno
          ? [
              ["", "Todas"],
              ["null", "Solo las que no están en el CRM"],
              ["not_null", "Solo las que ya están en el CRM"],
            ]
          : []
        ).map(([v, l]) => (
          <label key={v} className="flex items-center gap-2">
            <input
              type="radio"
              name="crm"
              className="size-4 accent-[var(--primary)]"
              checked={(f["company_id@is"] ?? "") === v}
              onChange={() => set({ "company_id@is": v || undefined })}
            />
            {l}
          </label>
        ))}
        <Check k="_ocultar_dnc" label='Ocultar "No contactar"' invert />
      </Section>
      <Section title="Investigación con IA" n={count(f, ["researched_at@is"])}>
        {[
          ["", "Todas"],
          ["not_null", "Solo investigadas"],
          ["null", "Sin investigar"],
        ].map(([v, l]) => (
          <label key={v} className="flex items-center gap-2">
            <input
              type="radio"
              name="investigada"
              className="size-4 accent-[var(--primary)]"
              checked={(f["researched_at@is"] ?? "") === v}
              onChange={() => set({ "researched_at@is": v || undefined })}
            />
            {l}
          </label>
        ))}
      </Section>
      {/* Bloque 0b: review data bought with SerpApi is owner-only */}
      {isDueno && <ReviewFilters />}
      <Section title="Fuente del dato" n={count(f, ["_fuentes"])}>
        <Chips
          k="_fuentes"
          values={FUENTES.map(([v]) => v)}
          label={(v) => FUENTES.find(([x]) => x === v)?.[1] ?? v}
        />
      </Section>
    </div>
  );
};

/** Reseñas de Google (PRD H.4): analizadas, por tema de queja y por calificación. */
const ReviewFilters = () => {
  const { f, set } = useF();
  const { data: T } = useReviewThemes();
  const themes = [...(T?.values() ?? [])].filter((t) => t.key !== "otro");
  return (
    <Section
      title="Reseñas de Google"
      n={count(f, ["_resenas", "_quejas", "_rating"])}
    >
      <Check k="_resenas" label="Solo con reseñas analizadas" />
      <span className="text-xs font-semibold text-muted-foreground">
        Se quejan de…
      </span>
      <Chips
        k="_quejas"
        values={themes.map((t) => t.key)}
        label={(v) => T?.get(v)?.label ?? v}
      />
      <label className="flex items-center gap-2">
        Calificación
        <select
          className="rounded-md border bg-background px-2 py-1"
          value={f._rating ?? ""}
          onChange={(e) => set({ _rating: e.target.value || undefined })}
        >
          <option value="">Cualquiera</option>
          {RATINGS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </label>
    </Section>
  );
};
